import { useEffect, useState } from "react";
import { ShipmentError } from "@/components/ShipmentError";
import { api, fmtDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Truck, Plus, Printer } from "lucide-react";
import { ShipmentDialog } from "@/components/ShipmentDialog";
import { openPdf } from "@/lib/produktion";
import { ShipmentLabelActions } from "@/components/ShipmentLabelActions";
import { VersandmitteilungButton } from "@/components/VersandmitteilungButton";
import { toast } from "sonner";
import { SectionBody, SectionHead, SectionSummary } from "@/components/SectionCollapse";

const SHIP_STATUSES = ["bezahlt", "abgeschlossen"];

const Inhalt = ({ s }) => {
  if (!s.gebucht) return null;
  const parts = [...s.inhalt.map((i) => `${i.menge} × ${i.bezeichnung}${i.zusaetzlich ? " (zusätzlich)" : ""}`), ...s.kundenteile_beigelegt.map((t) => `Kundenteil: ${t.bezeichnung}`)];
  const badge = s.zusatzpaket ? ["Zusätzliches Paket", "border-sky-300 text-sky-800"] : s.letztes_paket ? ["Letztes Paket", "border-emerald-300 text-emerald-800"] : ["Teillieferung", "border-amber-300 text-amber-800"];
  return (
    <div className="basis-full text-xs text-slate-600 pl-1" data-testid={`order-shipment-inhalt-${s.id}`}>
      <Badge variant="outline" className={`mr-2 ${badge[1]}`}>{badge[0]}</Badge>
      {parts.join(" · ") || (s.inhalt_text ? "" : "—")}
      {s.inhalt_text && <span className="whitespace-pre-line" data-testid={`order-shipment-inhalt-text-${s.id}`}>{parts.length ? " · " : ""}Inhalt / Grund: {s.inhalt_text}</span>}
      {s.rest.length > 0 && <span className="text-slate-400"> · danach offen: {s.rest.reduce((n, r) => n + r.menge, 0)} Stück</span>}
      {s.zusatz_versandposition_id && <span className="text-slate-400"> · Versandkosten berechnet</span>}
    </div>
  );
};

export const OrderShipmentsSection = ({ order, readOnly, onChanged, onMailSent, collapseKey }) => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/versand", { params: { order_id: order.id } }).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, [order.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const canShip = !readOnly && SHIP_STATUSES.includes(order.status); // auch nach vollständigem Versand: zusätzliches Paket
  const packzettel = (s) => openPdf(`/versand/${s.id}/packzettel.pdf`, `Packzettel_${order.auftragsnummer}.pdf`).catch((e) => toast.error(`Packzettel konnte nicht erzeugt werden. ${e.message}`));

  return (
    <div className="space-y-3" data-testid="order-shipments-section">
      <SectionHead k={collapseKey} label="Versand" className="flex items-center gap-2">
        <div className="flex items-center gap-2 font-semibold text-slate-900"><Truck className="w-4 h-4" /> Versand</div>
        <SectionSummary k={collapseKey}>{items.length === 0 ? "noch kein Versand" : `${items.length} ${items.length === 1 ? "Sendung" : "Sendungen"}`}</SectionSummary>
        {canShip && <Button size="sm" className="ml-auto" onClick={() => setOpen(true)} data-testid="order-create-shipment-button"><Plus className="w-4 h-4 mr-1" /> {order.versand_status === "versendet" ? "Zusätzliches Paket" : items.some((s) => s.gebucht) ? "Nächstes Paket" : "Versand erzeugen"}</Button>}
      </SectionHead>
      <SectionBody k={collapseKey}>
      {items.length === 0 ? (
        <p className="text-sm text-slate-500">{canShip ? "Noch kein Versand angelegt." : "Versand ist ab Status „bezahlt“ möglich."}</p>
      ) : (
        <ul className="divide-y divide-slate-100 text-sm">
          {items.map((s) => (
            <li key={s.id} className="py-2 flex items-center gap-3 flex-wrap" data-testid={`order-shipment-${s.id}`}>
              <span className="font-mono text-xs" data-testid={`order-shipment-tracking-${s.id}`}>{s.tracking_number || "—"}</span>
              <Badge variant={s.status === "fehler" ? "destructive" : "outline"}>{s.status}</Badge>
              <span className="text-slate-600">{s.ohne_label ? `Ohne DHL-Label${s.versanddienst ? ` · ${s.versanddienst}` : ""}` : `${s.versandart === "kleinpaket" ? "Kleinpaket" : "DHL Paket"} · ${String(s.gewicht_kg).replace(".", ",")} kg`}</span>
              <span className="text-xs text-slate-400">{fmtDateTime(s.created_at)}</span>
              <ShipmentError error={s.error} details={s.error_details} testid={`order-shipment-error-${s.id}`} />
              <div className="ml-auto flex gap-2 flex-wrap justify-end">
                {s.gebucht && <Button size="sm" variant="outline" onClick={() => packzettel(s)} data-testid={`order-shipment-packzettel-${s.id}`}><Printer className="w-4 h-4 mr-1" /> Packzettel</Button>}
                <ShipmentLabelActions s={s} auftragsnummer={order.auftragsnummer} onChanged={load} />
                {!readOnly && (s.gebucht || s.label_url) && <VersandmitteilungButton s={s} onSent={() => { load(); onChanged?.(); onMailSent?.(); }} />}
              </div>
              <Inhalt s={s} />
            </li>
          ))}
        </ul>
      )}
      </SectionBody>
      <ShipmentDialog open={open} onOpenChange={setOpen} fixedOrder={order} folgepaket={items.some((s) => s.gebucht)} onCreated={() => { load(); onChanged?.(); }} />
    </div>
  );
};
